import { all, call, fork, put, takeEvery, select } from "redux-saga/effects";
import {
  CALENDARIO_INICIA,
  CALENDARIO_NOVO,
  CALENDARIO_SALVA,
  CALENDARIO_BUSCA_EDICAO,
  CALENDARIO_LISTA_TODOS,
  CALENDARIO_LISTA_ESCOLAS,
  CALENDARIO_CARREGA_DIAS,
  CALENDARIO_CARREGA_TIPOS_EVENTO,
  CALENDARIO_LISTA_TURMAS,
  CALENDARIO_SALVA_ANOTACAO,
  CALENDARIO_REMOVE_ANOTACAO,
  CALENDARIO_SALVA_EVENTO_DIA,
  CALENDARIO_REMOVE_EVENTO_DIA
} from 'constants/CalendarioTypes';
import {
  salvaCalendarioSucesso,
  buscaCalendarioEdicaoSucesso,
  listaCalendariosSucesso,
  listaEscolasSucesso,
  carregaDiasCalendarioSucesso,
  carregaTiposEventoSucesso,
  listaTurmasSucesso,
  calendarioErro,
  carregaDiasCalendario,
  carregaTiposEvento,
  listaTurmas,
  listaCalendarios
} from 'actions/Calendario';
import { showAuthMessage } from 'actions/Auth';
import { ON_SHOW_LOADER, ON_HIDE_LOADER, ON_SHOW_SUCCESS_MESSAGE } from 'constants/ActionTypes';
import requisicaoApi from 'util/Api';

// API calls
const postCalendario = async (data) => {
  // Ajuste para payload esperado: { idEscola, ano }
  const payload = {
    idEscola: data.escola_id,
    ano: data.ano
  };
  return await requisicaoApi.post('/calendarios', payload)
    .then(res => res)
    .catch(err => err);
};

const getCalendario = async (id) =>
  await requisicaoApi.get(`/calendarios/${id}`)
    .then(res => res)
    .catch(err => err);

const getDiasCalendario = async (calendarioId, ano) => {
  const inicio = `${ano}-01-01`;
  const fim = `${ano}-12-31`;
  const eventosReq = requisicaoApi.get(`/calendarios/${calendarioId}/eventos/periodo?inicio=${inicio}&fim=${fim}`);
  const anotacoesReq = requisicaoApi.get(`/calendarios/${calendarioId}/anotacoes/periodo?inicio=${inicio}&fim=${fim}`);
  return await Promise.all([eventosReq, anotacoesReq])
    .then(([eventosRes, anotacoesRes]) => {
      // Unifica resposta
      // Se algum falhar, trata erro. Aqui assumimos sucesso.
      const eventos = eventosRes.data || [];
      const anotacoes = anotacoesRes.data || [];
      
      // Combinar em uma estrutura única por dia para o reducer
      // Mapa dia -> { data, evento, anotacoes: [] }
      const mapaDias = {};
      
      eventos.forEach(ev => {
        const data = (ev.dataEvento || '').substring(0, 10);
        if (!mapaDias[data]) mapaDias[data] = { data, anotacoes: [] };
        
        // Se já tiver um evento para o dia, não sobrescreve, mas idealmente o back não deveria retornar duplicados
        // para o mesmo dia/escola. Mas o usuário reportou "tem um evento por dia... quando salvo some"
        // O back pode retornar duplicatas se houver erro de integridade, mas vamos usar o último ou tratar lista?
        // O front assume 1 evento por dia.
        mapaDias[data].evento = ev;
      });
      
      anotacoes.forEach(an => {
        const data = ((an.dataAnotacao || an.data) || '').substring(0, 10);
        if (!mapaDias[data]) mapaDias[data] = { data, anotacoes: [] };
        mapaDias[data].anotacoes.push({
          id: an.id,
          anotacao: an.resumoAnotacao || an.anotacao,
          descricao: an.descricao,
          dataAnotacao: an.dataAnotacao || an.data
        });
      });
      
      return { status: 200, data: Object.values(mapaDias) };
    })
    .catch(err => err);
}

const getTiposEvento = async (escolaId) =>
  await requisicaoApi.get(`/escolas/${escolaId}/tipos-evento`)
    .then(res => res)
    .catch(err => err);

const getTurmas = async (escolaId) =>
  await requisicaoApi.get(`/turmas?escolaId=${escolaId}`)
    .then(res => res)
    .catch(err => err);

const postAnotacao = async (data) => {
  const payload = {
    idCalendario: data.calendario_id,
    resumoAnotacao: data.anotacao,
    descricao: data.descricao, // Adicionando caso backend aceite, payload mínimo pede resumoAnotacao
    dataAnotacao: data.data
  };
  return await requisicaoApi.post('/anotacoes-calendario', payload)
    .then(res => res)
    .catch(err => err);
};

const deleteAnotacao = async (id) =>
  // Endpoint de delete não foi explicitado, assumindo DELETE /api/anotacoes-calendario/{id}
  await requisicaoApi.delete(`/anotacoes-calendario/${id}`)
    .then(res => res)
    .catch(err => err);

const postEventoDia = async (data) => {
  // Este endpoint não foi explicitado para CRIAÇÃO de evento único
  // Mas temos GET /api/calendarios/{id}/eventos
  // Assumindo POST /api/calendarios/{id}/eventos ou similar
  // O prompt diz "Eventos do Calendário ... Lista eventos". Não deu POST de evento explicitamente,
  // mas deu "PUT /api/calendarios/{id} -> atualiza calendario".
  // Talvez eventos sejam criados via sub-recurso ou endpoint específico não listado?
  // Vou usar um endpoint coerente com o padrão REST sugerido: POST /api/calendarios/{id}/eventos
  // Payload inferido: { idCalendario, dataEvento, idTipoEvento, turmasAfetadas: [...] }
  
  const payload = {
    idCalendario: data.calendario_id,
    dataEvento: data.data,
    idTipoEvento: data.nm_motivo, // nm_motivo no front estava guardando o ID do tipo
    descricao: data.descricao, // Adiciona descrição ao payload
    // turmasAfetadas deve ser array de IDs ou objetos? Retorno GET mostra array de objetos.
    // Envio deve ser provavelmente array de IDs de turma.
    turmasAfetadas: data.turmas // Ajustado para turmasIds conforme solicitado
  };
  
  // Como não tenho endpoint POST evento, vou tentar inferir ou usar um placeholder
  // Se o usuário mandou "Eventos do Calendário GET ...", mas não POST,
  // E mandou Tipos de Evento POST, Anotações POST...
  // Vou assumir POST /api/eventos-calendario ou POST /api/calendarios/{id}/eventos
  return await requisicaoApi.post(`/calendarios/${data.calendario_id}/eventos`, payload)
    .then(res => res)
    .catch(err => err);
};

const putEventoDia = async (data) => {
  const payload = {
    idCalendario: data.calendario_id,
    dataEvento: data.data,
    idTipoEvento: data.nm_motivo,
    descricao: data.descricao,
    turmasAfetadas: data.turmas
  };
  
  // Assumindo PUT /api/calendarios/eventos/{id} ou similar
  return await requisicaoApi.put(`/calendarios/eventos/${data.id}`, payload)
    .then(res => res)
    .catch(err => err);
};

const deleteEventoDia = async (id) =>
  await requisicaoApi.delete(`/calendarios/eventos/${id}`) // Ajuste conforme endpoint real
    .then(res => res)
    .catch(err => err);

const getCalendarios = async (escolaId) => {
  let url = '/calendarios';
  if (escolaId) {
    url += `?EscolaId=${escolaId}`;
  }
  return await requisicaoApi.get(url)
    .then(res => res)
    .catch(err => err);
}

const getEscolas = async () =>
  await requisicaoApi.get('/escolas')
    .then(res => res)
    .catch(err => err);

// Helpers
const getMensagemErro = (ret, fallback) => {
  const status = ret && ret.response && ret.response.status;
  const data = ret && ret.response && ret.response.data;
  
  if (status === 400 || status === 406) {
    const inconsistencias = (data && data.inconsistencias) || [];
    if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
      return inconsistencias[0].mensagem || JSON.stringify(data);
    }
    return (data && (data.mensagem || data.message)) ? (data.mensagem || data.message) : JSON.stringify(data);
  }
  
  if (ret && ret.message) return JSON.stringify(ret.message);
  return fallback || 'Falha na operação.';
};

// Sagas
function* fnListaCalendarios({ payload }) {
  yield put({ type: ON_SHOW_LOADER });
  try {
    const res = yield call(getCalendarios, payload);
    if (res.status === 200) {
      yield put(listaCalendariosSucesso(res.data));
    } else {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao carregar lista de calendários')));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnListaEscolas() {
  try {
    const res = yield call(getEscolas);
    if (res.status === 200) {
      // API pode retornar array ou objeto único dependendo da permissão/contexto
      // Garantir que seja array para o reducer e componente
      let lista = [];
      
      if (Array.isArray(res.data)) {
        lista = res.data;
      } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
        // Alguns endpoints retornam { data: [...], ... }
        lista = res.data.data;
      } else if (res.data && res.data.items && Array.isArray(res.data.items)) {
         // Alguns endpoints paginados retornam items
         lista = res.data.items;
      } else if (res.data && typeof res.data === 'object') {
        // Se retornou um objeto único (não null), transforma em array
        // Cuidado com objetos vazios ou estruturas inesperadas
        const values = Object.values(res.data);
        if (values.length > 0 && typeof values[0] === 'object') {
            // Pode ser um map { "1": {id:1...}, "2": {id:2...} }
             lista = values;
        } else {
             lista = [res.data];
        }
      }

      yield put(listaEscolasSucesso(lista));
    } else {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao listar escolas')));
    }
  } catch (error) {
    console.error('SagaCalendario: fnListaEscolas error', error);
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnSalvaCalendario({ payload }) {
  yield put({ type: ON_SHOW_LOADER });
  try {
    const res = yield call(postCalendario, payload);
    if (res.status === 200 || res.status === 201) {
      const calendarioSalvo = res.data;
      
      // O backend não está retornando o objeto calendário criado, apenas uma mensagem de sucesso.
      // Precisamos buscar o calendário criado ou assumir os dados enviados se possível,
      // mas o ideal é que o backend retornasse o ID.
      // Como fallback, se o backend retornar sucesso mas sem ID, vamos tentar buscar o calendário
      // usando os dados que enviamos (escola e ano) se houver um endpoint de busca por filtro,
      // OU, se não tiver jeito, assumir que precisamos listar todos ou algo assim.
      
      // Mas espera, se eu acabei de criar, eu tenho os dados do payload!
      // Payload de envio: { escola_id, ano }
      // O backend deveria retornar o ID gerado. Se não retorna, temos um problema de contrato.
      // Olhando o log: {sucesso: true, mensagem: 'Inclusão realizada com sucesso.', inconsistencias: null}
      // Realmente não tem ID.
      
      // TENTATIVA DE CONTORNO:
      // Vamos listar os calendários novamente e tentar encontrar o que acabamos de criar pelo escola_id e ano.
      
      yield put(listaCalendarios()); // Dispara atualização da lista
      
      // Não podemos fazer muito mais sem o ID. 
      // Vamos tentar disparar uma ação para avisar que salvou, mas não temos o ID para redirecionar direto.
      // Porém, o usuário quer ser redirecionado.
      
      // Se eu listar os calendários e pegar o último ou filtrar?
      // É uma race condition perigosa, mas talvez seja a única opção sem alterar o backend.
      
      // Vamos tentar fazer uma chamada manual de getCalendarios aqui mesmo para pegar o ID.
      try {
         // Otimização: filtrar pela escola do calendário criado para reduzir busca
         const listaRes = yield call(getCalendarios, payload.escola_id);
         if (listaRes.status === 200 && Array.isArray(listaRes.data)) {
             const criado = listaRes.data.find(c => c.idEscola == payload.escola_id && c.ano == payload.ano);
             if (criado) {
                 yield put(salvaCalendarioSucesso(criado));
                 yield put(carregaDiasCalendario(criado.id, criado.ano));
                 return;
             }
         }
      } catch (e) {
         console.error('SagaCalendario: Erro ao tentar recuperar calendário criado', e);
      }

      // Se não encontrou ou deu erro, segue o fluxo antigo (que vai falhar no redirecionamento mas avisa sucesso)
      yield put(salvaCalendarioSucesso(calendarioSalvo));
      
      // Validação antes de chamar carregaDiasCalendario
      const id = calendarioSalvo.id;
      const ano = calendarioSalvo.ano;
      
      if (id && ano) {
          yield put(carregaDiasCalendario(id, ano));
      } else {
          console.error('SagaCalendario: Resposta de salvar calendário incompleta. Não foi possível redirecionar.', calendarioSalvo);
          // Forçar um feedback visual de que salvou mas não redirecionou
          yield put({ type: ON_SHOW_SUCCESS_MESSAGE, payload: 'Calendário criado com sucesso. Por favor, selecione-o na lista.' });
      }
    } else {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao salvar calendário')));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnBuscaCalendarioEdicao({ payload }) {
  yield put({ type: ON_SHOW_LOADER });
  try {
    const res = yield call(getCalendario, payload);
    if (res.status === 200) {
      yield put(buscaCalendarioEdicaoSucesso(res.data));
      yield put(carregaDiasCalendario(res.data.id, res.data.ano));
      
      const idEscola = res.data.escola_id || res.data.idEscola || res.data.IdEscola;
      if (idEscola) {
        yield put(carregaTiposEvento(idEscola));
        yield put(listaTurmas(idEscola));
      }
    } else {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao buscar calendário')));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnCarregaDias({ payload }) {
  yield put({ type: ON_SHOW_LOADER });
  try {
    const { calendarioId, ano } = payload;
    
    // Validação de entrada
    if (!calendarioId || !ano || calendarioId === 'undefined' || ano === 'undefined') {
        console.warn('SagaCalendario: Tentativa de carregar dias com parâmetros inválidos', payload);
        yield put({ type: ON_HIDE_LOADER });
        return;
    }

    const res = yield call(getDiasCalendario, calendarioId, ano);
    if (res.status === 200) {
      yield put(carregaDiasCalendarioSucesso(res.data));
    } else {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao carregar dias')));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnCarregaTiposEvento({ payload }) {
  try {
    const res = yield call(getTiposEvento, payload);
    if (res.status === 200) {
      yield put(carregaTiposEventoSucesso(res.data));
    } else {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao listar tipos de evento')));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnListaTurmas({ payload }) {
  try {
    const res = yield call(getTurmas, payload);
    if (res.status === 200) {
      yield put(listaTurmasSucesso(res.data));
    } else {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao listar turmas')));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnSalvaAnotacao({ payload }) {
  yield put({ type: ON_SHOW_LOADER });
  try {
    const res = yield call(postAnotacao, payload);
    if (res.status === 200 || res.status === 201) {
      yield put({ type: ON_SHOW_SUCCESS_MESSAGE, payload: 'Anotação salva com sucesso' });
      const calendarioAtual = yield select(state => state.calendario.calendarioAtual);
      const calendarioId = calendarioAtual && calendarioAtual.id ? calendarioAtual.id : payload.calendario_id;
      const ano = calendarioAtual && calendarioAtual.ano ? calendarioAtual.ano : ((payload.data || '').substring(0,4));
      if (calendarioId && ano) {
        yield put(carregaDiasCalendario(calendarioId, ano));
      }
    } else {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao salvar anotação')));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnRemoveAnotacao({ payload }) {
  yield put({ type: ON_SHOW_LOADER });
  try {
    const res = yield call(deleteAnotacao, payload);
    if (res.status !== 200 && res.status !== 204) {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao remover anotação')));
    } else {
      const calendarioAtual = yield select(state => state.calendario.calendarioAtual);
      if (calendarioAtual && calendarioAtual.id && calendarioAtual.ano) {
        yield put(carregaDiasCalendario(calendarioAtual.id, calendarioAtual.ano));
      }
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnSalvaEventoDia({ payload }) {
  yield put({ type: ON_SHOW_LOADER });
  try {
    let res;
    if (payload.id) {
        res = yield call(putEventoDia, payload);
    } else {
        res = yield call(postEventoDia, payload);
    }
    
    if (res.status !== 200 && res.status !== 201) {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao salvar evento do dia')));
    } else {
      yield put({ type: ON_SHOW_SUCCESS_MESSAGE, payload: 'Configuração do dia salva com sucesso' });
      const calendarioAtual = yield select(state => state.calendario.calendarioAtual);
      const calendarioId = calendarioAtual && calendarioAtual.id ? calendarioAtual.id : payload.calendario_id;
      const ano = calendarioAtual && calendarioAtual.ano ? calendarioAtual.ano : ((payload.data || '').substring(0,4));
      if (calendarioId && ano) {
        yield put(carregaDiasCalendario(calendarioId, ano));
      }
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnRemoveEventoDia({ payload }) {
  yield put({ type: ON_SHOW_LOADER });
  try {
    const res = yield call(deleteEventoDia, payload);
    if (res.status !== 200 && res.status !== 204) {
      yield put(showAuthMessage(getMensagemErro(res, 'Erro ao remover evento do dia')));
    } else {
      yield put({ type: ON_SHOW_SUCCESS_MESSAGE, payload: 'Configuração restaurada com sucesso' });
      const calendarioAtual = yield select(state => state.calendario.calendarioAtual);
      if (calendarioAtual && calendarioAtual.id && calendarioAtual.ano) {
        yield put(carregaDiasCalendario(calendarioAtual.id, calendarioAtual.ano));
      }
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

// Watchers
export function* watchListaCalendarios() {
  yield takeEvery(CALENDARIO_LISTA_TODOS, fnListaCalendarios);
}

export function* watchListaEscolas() {
  yield takeEvery(CALENDARIO_LISTA_ESCOLAS, fnListaEscolas);
}

export function* watchSalvaCalendario() {
  yield takeEvery(CALENDARIO_SALVA, fnSalvaCalendario);
}

export function* watchBuscaCalendario() {
  yield takeEvery(CALENDARIO_BUSCA_EDICAO, fnBuscaCalendarioEdicao);
}

export function* watchCarregaDias() {
  yield takeEvery(CALENDARIO_CARREGA_DIAS, fnCarregaDias);
}

export function* watchCarregaTiposEvento() {
  yield takeEvery(CALENDARIO_CARREGA_TIPOS_EVENTO, fnCarregaTiposEvento);
}

export function* watchListaTurmas() {
  yield takeEvery(CALENDARIO_LISTA_TURMAS, fnListaTurmas);
}

export function* watchSalvaAnotacao() {
  yield takeEvery(CALENDARIO_SALVA_ANOTACAO, fnSalvaAnotacao);
}

export function* watchRemoveAnotacao() {
  yield takeEvery(CALENDARIO_REMOVE_ANOTACAO, fnRemoveAnotacao);
}

export function* watchSalvaEventoDia() {
  yield takeEvery(CALENDARIO_SALVA_EVENTO_DIA, fnSalvaEventoDia);
}

export function* watchRemoveEventoDia() {
  yield takeEvery(CALENDARIO_REMOVE_EVENTO_DIA, fnRemoveEventoDia);
}

export default function* rootSaga() {
  yield all([
    fork(watchListaCalendarios),
    fork(watchListaEscolas),
    fork(watchSalvaCalendario),
    fork(watchBuscaCalendario),
    fork(watchCarregaDias),
    fork(watchCarregaTiposEvento),
    fork(watchListaTurmas),
    fork(watchSalvaAnotacao),
    fork(watchRemoveAnotacao),
    fork(watchSalvaEventoDia),
    fork(watchRemoveEventoDia)
  ]);
}
